// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';

// model imports
import { CompletedConveyanceResultDto } from '../_models/completed-property-conveyance-result-dto';
import { OpenPropertyConveyanceResultDto } from '../_models/open-property-conveyance-result-dto';
import { PropertyConveyanceParameterDto } from '../_models/completed-property-conveyance-parameter-dto';
import { ConveyanceEditCommunityParametersDto } from '../_models/conveyance-edit-community-parameters-dto';
import { ConveyanceWorkQueueParametersDto } from '../_models/conveyance-work-queue-parameters-dto';
import { ConveyanceCommunitySelectorResultDto } from '../_models/conveyance-community-selector-result-dto';
import { SearchConveyencePropertyParametersDto } from '../_models/search-conveyance-property-parameters-dto';
import { ConveyedPropertyParametersDto } from '../_models/conveyance-property-parameters-dto';
import { SearchConveyencePropertyResultDto } from '../_models/search-conveyance-property-result-dto';
import { ConveyedPropertyValidateResultDto } from '../_models/conveyed-property-validate-result-dto';
import { ConveyanceWelcomeEditorResultDto } from '../_models/conveyance-welcome-editor-result-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { ConveyanceWelcomeEditorValidateParametersDto } from '../_models/conveyance-welcome-editor-validate-parameter-dto';
import { BuyerOwnerEditorResultDto } from '../_models/buyer-owner-editor-result-dto';
import { ConveyanceBuyerCommunitySelectorParametersDto } from '../_models/conveyance-buyer-community-selector-parameter-dto';
import { BuyerOwnerResultDto } from '../_models/buyer-owner-result-dto';
import { SearchBuyerOwnersParametersDto } from '../_models/search-buyer-owners-parameters-dto';
import { BuyerOwnerValidationResultDto } from '../_models/buyer-owner-validation-result-dto';
import { ConveyanceBuyerOwnerParametersDto } from '../_models/conveyance-buyer-owner-parameters-dto';
import { OwnerPropertyParametersDto } from '../_models/owner-property-parameters-dto';
import { OwnerPropertyResultDto } from '../_models/owner-property-result-dto';
import { ConveyanceAssessmentEditorResultDto } from '../_models/conveyance-assessment-editor-result-dto';
import { ConveyanceBillingRuleAssignmentsParametersDto } from '../_models/conveyance-billing-rule-assignments-parameters-dto';
import { ConveyanceBillingRuleAssignmentResultDto } from '../_models/conveyance-billing-rule-assignment-result-dto';
import { ConveyanceClosingFeeEditorResultDto } from '../_models/conveyance-closing-fee-editor-result-dto';
import { ConveyanceEditorResultDto } from '../_models/conveyance-editor-result-dto';
import { ConveyanceCommunitySoaRequestParametersDto } from '../_models/conveyance-community-soa-request-parameter-dto';
import { ConveyanceCommunitySoaRequestResultDto } from '../_models/conveyance-community-soa-request-result-dto';
import { ConveyanceCommunitySoaFulfillmentParametersDto } from '../_models/conveyance-community-soa-filfillment-parameters-dto';
import { ConveyanceCommunitySoaFulfillmentResultDto } from '../_models/conveyance-community-soa-fulfillment-result-dto';
import { ConveyanceSoaAttachmentResultDto } from '../_models/conveyance-soa-attachment-result-dto';
import { ConveyanceSoaRequestAttachmentDownloadParameterDto } from '../_models/conveyance-soa-request-attachment-download-parameter-dto';
import { ConveyanceCommunityFeeParametersDto } from '../_models/conveyance-community-fee-parameters-dto';
import { ConveyanceCommunityFeeResultDto } from '../_models/conveyance-community-fee-result-dto';
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';
import { ValidateConveyanceAssessmentRulesParametersDto } from '../_models/validate-conveyance-assessment-rules-parameters-dto';
import { ValidateConveyanceClosingFeeRulesParametersDto } from '../_models/validate-conveyance-closing-fee-rules-parameters-dto';
import { ConveyanceClosingDateValidateParametersDto } from '../_models/conveyance-closing-date-validate-parameters-dto';
import { ConveyanceClosingDateValidationResultDto } from '../_models/conveyance-closing-date-validation-result-dto';
import { ProcessConveyanceParametersDto } from '../_models/process-conveyance-parameters-dto';
import { ConveyanceNotesBaseParametersDto } from '../_models/conveyance-notes-base-parameters-dto';
import { ConveyanceAddNotesParametersDto } from '../_models/conveyance-add-notes-parameters-dto';
import { ConveyancePropertyRawDto } from '../_models/conveyance-property-raw-dto';
import { UnconveyancePropertyEditorResultDto } from '../_models/unconveyance-property-editor-result-dto';
import { UnconveyPropertyResultDto } from '../_models/unconvey-property-result-dto';

@Injectable({
    providedIn: 'root'
})
export class PropertyConveyanceService {
    constructor(private readonly httpClient: HttpClient) {}

    private _conveyanceCommunityChangeSubject = new Subject<ConveyanceCommunitySelectorResultDto>();
    private _conveyancePropertyChangeSubject = new Subject<ConveyedPropertyParametersDto>();
    private _buyerOwnerChangeSubject = new Subject<ConveyanceBuyerOwnerParametersDto>();

    private _moveToNextStepSubject = new Subject<void>();

    public onConveyanceCommunityChange(): Subject<ConveyanceCommunitySelectorResultDto> {
        return this._conveyanceCommunityChangeSubject;
    }

    public changeConveyanceCommunity(value: ConveyanceCommunitySelectorResultDto): void {
        this._conveyanceCommunityChangeSubject.next(value);
    }

    public onConveyancePropertyChange(): Subject<ConveyedPropertyParametersDto> {
        return this._conveyancePropertyChangeSubject;
    }

    public changeConveyanceProperty(value: ConveyedPropertyParametersDto): void {
        this._conveyancePropertyChangeSubject.next(value);
    }

    public onBuyerOwnerChange(): Subject<ConveyanceBuyerOwnerParametersDto> {
        return this._buyerOwnerChangeSubject;
    }

    public changeBuyerOwner(value: ConveyanceBuyerOwnerParametersDto): void {
        this._buyerOwnerChangeSubject.next(value);
    }

    public onMoveToNextStepChange(): Subject<void> {
        return this._moveToNextStepSubject;
    }

    public moveToNextStep(): void {
        this._moveToNextStepSubject.next();
    }

    getOpenWorkQueue(): Observable<OpenPropertyConveyanceResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/open`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(OpenPropertyConveyanceResultDto, response)));
    }

    getCompletedWorkQueue(baseParams: PropertyConveyanceParameterDto): Observable<CompletedConveyanceResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/completed${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CompletedConveyanceResultDto, response)));
    }

    getNoteDetails(baseParams: ConveyanceNotesBaseParametersDto): Observable<BaseProcessNotesResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/notes${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(BaseProcessNotesResultDto, response)));
    }

    getCommunityEditorDetails(): Observable<CommunitySelectorResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/editor`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CommunitySelectorResultDto, response)));
    }

    getProcessCommunities(
        baseParams: ConveyanceWorkQueueParametersDto
    ): Observable<ConveyanceCommunitySelectorResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/communities${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(ConveyanceCommunitySelectorResultDto, response)));
    }

    getSearchProperties(
        baseParams: SearchConveyencePropertyParametersDto
    ): Observable<SearchConveyencePropertyResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/search/properties`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(SearchConveyencePropertyResultDto, response)));
    }

    validateProperty(baseParams: ConveyedPropertyParametersDto): Observable<ConveyedPropertyValidateResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyedPropertyValidateResultDto, response)));
    }

    welcomeEditorDetails(baseParams: ConveyedPropertyParametersDto): Observable<ConveyanceWelcomeEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/welcome/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyanceWelcomeEditorResultDto, response)));
    }

    welcomeEditorValidate(baseParams: ConveyanceWelcomeEditorValidateParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/property/welcome/editor/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    buyerOwnerEditor(baseParams: ConveyedPropertyParametersDto): Observable<BuyerOwnerEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(BuyerOwnerEditorResultDto, response)));
    }

    buyerOwnerCommunities(
        baseParams: ConveyanceBuyerCommunitySelectorParametersDto
    ): Observable<ConveyanceCommunitySelectorResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/communities`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceCommunitySelectorResultDto, response)));
    }

    searchBuyerOwner(baseParams: SearchBuyerOwnersParametersDto): Observable<BuyerOwnerResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/search/buyer/owners`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(BuyerOwnerResultDto, response)));
    }

    buyerOwnerValidate(baseParams: ConveyanceBuyerOwnerParametersDto): Observable<BuyerOwnerValidationResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(BuyerOwnerValidationResultDto, response)));
    }

    buyerOwnerProperties(baseParams: OwnerPropertyParametersDto): Observable<OwnerPropertyResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/buyer/owner/properties`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(OwnerPropertyResultDto, response)));
    }

    assessmentRuleEditor(
        baseParams: ConveyanceBuyerOwnerParametersDto
    ): Observable<ConveyanceAssessmentEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/assessment/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyanceAssessmentEditorResultDto, response)));
    }

    assessmentRuleValidate(baseParams: ValidateConveyanceAssessmentRulesParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/assessment/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    sellerCurrentBillingRule(
        baseParams: ConveyanceBillingRuleAssignmentsParametersDto
    ): Observable<ConveyanceBillingRuleAssignmentResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/billingruleassignments`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceBillingRuleAssignmentResultDto, response)));
    }

    saveNote(baseParams: ConveyanceAddNotesParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/notes`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    saveCommunity(baseParams: ConveyanceEditCommunityParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }

    clearCommunity(baseParams: ConveyanceWorkQueueParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/clear`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }

    updateWorkQueueStatus(baseParams: ConveyedPropertyParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/status/collection`;
        return this.httpClient.put(url, classToPlain(baseParams));
    }

    deleteConveyance(baseParams: ConveyanceWorkQueueParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }

    getClosingFeeEditor(
        baseParams: ConveyanceBuyerOwnerParametersDto
    ): Observable<ConveyanceClosingFeeEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingfee/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyanceClosingFeeEditorResultDto, response)));
    }

    closingFeeRuleValidate(baseParams: ValidateConveyanceClosingFeeRulesParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingfee/validate`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    getConveyanceEditor(baseParams: ConveyanceBuyerOwnerParametersDto): Observable<ConveyanceEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/editor`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyanceEditorResultDto, response)));
    }

    getConveyanceSoaRequests(
        baseParams: ConveyanceCommunitySoaRequestParametersDto
    ): Observable<ConveyanceCommunitySoaRequestResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequests`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceCommunitySoaRequestResultDto, response)));
    }

    getConveyanceSoaFulfillments(
        baseParams: ConveyanceCommunitySoaFulfillmentParametersDto
    ): Observable<ConveyanceCommunitySoaFulfillmentResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soafulfillments`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceCommunitySoaFulfillmentResultDto, response)));
    }

    getSoaRequestAttachment(
        baseParams: ConveyanceCommunitySoaFulfillmentParametersDto
    ): Observable<ConveyanceSoaAttachmentResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequest/attachment`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceSoaAttachmentResultDto, response)));
    }

    downloadSoaRequestAttachment(baseParams: ConveyanceSoaRequestAttachmentDownloadParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/soarequest/attachment/download${baseParams.getQueryString}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((response: any) => new Blob([response])));
    }

    getConveyanceCommunityFees(
        baseParams: ConveyanceCommunityFeeParametersDto
    ): Observable<ConveyanceCommunityFeeResultDto[]> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/community/fees`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any[]) => plainToClass(ConveyanceCommunityFeeResultDto, response)));
    }

    closingDateValidate(
        baseParams: ConveyanceClosingDateValidateParametersDto
    ): Observable<ConveyanceClosingDateValidationResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/closingdate/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(ConveyanceClosingDateValidationResultDto, response)));
    }

    processConveyance(baseParams: ProcessConveyanceParametersDto): Observable<any> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/process`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    getPaperWorkFile(fileUrl: string): Observable<Blob> {
        return this.httpClient
            .get(fileUrl, { responseType: 'arraybuffer' })
            .pipe(map((response: any) => new Blob([response])));
    }

    getUnconveyPropertyEditor(
        conveyedProperty: ConveyancePropertyRawDto
    ): Observable<UnconveyancePropertyEditorResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/unconvey/editor${conveyedProperty.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(
                map((response: UnconveyancePropertyEditorResultDto) =>
                    plainToClass(UnconveyancePropertyEditorResultDto, response)
                )
            );
    }

    unconveyProperty(conveyedProperty: ConveyancePropertyRawDto): Observable<UnconveyPropertyResultDto> {
        const url = `${URLConstants.ccPropertyConveyanceApiUrl}/unconvey`;
        return this.httpClient
            .put(url, classToPlain(conveyedProperty))
            .pipe(map((response: any) => plainToClass(UnconveyPropertyResultDto, response)));
    }
}
